import { createTheme } from '@mui/material/styles';

export const colors = {
  mint: '#4BE2BD',
  mintSoft: '#E1FAF3',
  mintDeep: '#1F9F86',
  ink900: '#161514',
  ink800: '#232222',
  ink700: '#3A3A38',
  ink500: '#6B6967',
  ink400: '#8E8B88',
  ink300: '#BABAB6',
  ink200: '#E5E3DF',
  ink150: '#EDEBE6',
  ink100: '#F4F2EE',
  ink50: '#FAF9F6',
  paper: '#FCFBF8',
  white: '#FFFFFF',
  error: '#D64545',
  warning: '#B66A0A',
  success: '#167A62',
} as const;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: colors.ink900, light: colors.ink800, dark: '#000000', contrastText: colors.white },
    background: { default: colors.paper, paper: colors.white },
    text: { primary: colors.ink800, secondary: colors.ink500, disabled: colors.ink400 },
    error: { main: colors.error },
    warning: { main: colors.warning },
    success: { main: colors.success },
    divider: colors.ink150,
  },
  typography: {
    fontFamily: '"Inter Tight", Inter, system-ui, -apple-system, sans-serif',
    h1: { fontSize: 28, fontWeight: 600, letterSpacing: '-0.025em' },
    h2: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.022em' },
    h3: { fontSize: 18, fontWeight: 600, letterSpacing: '-0.015em' },
    h4: { fontSize: 16, fontWeight: 600, letterSpacing: '-0.012em' },
    h5: { fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' },
    h6: { fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' },
    body1: { fontSize: 14, lineHeight: 1.5 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    caption: { fontSize: 12, color: colors.ink500 },
    button: { fontSize: 13, fontWeight: 500, textTransform: 'none' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: colors.paper, color: colors.ink800 } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          minHeight: 38,
          paddingInline: 18,
          paddingBlock: 8,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        sizeLarge: { paddingInline: 22, paddingBlock: 11, fontSize: 14 },
        contained: {
          color: colors.white,
          backgroundColor: colors.ink900,
          '&:hover': { backgroundColor: '#000000' },
        },
        outlined: {
          borderColor: colors.ink200,
          color: colors.ink800,
          backgroundColor: colors.white,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small', fullWidth: true },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.white,
            borderRadius: 10,
            fontSize: 13.5,
            '& fieldset': { borderColor: colors.ink200 },
            '&:hover fieldset': { borderColor: colors.ink300 },
            '&.Mui-focused fieldset': { borderColor: colors.ink800, borderWidth: 1 },
          },
          '& .MuiInputLabel-root': { fontSize: 13, color: colors.ink500 },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { backgroundColor: colors.white, fontSize: 13.5 },
        notchedOutline: { borderColor: colors.ink200 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${colors.ink200}`,
          boxShadow: '0 1px 0 rgba(20,20,20,.02), 0 1px 2px rgba(20,20,20,.04)',
        },
      },
    },
  },
});
